import {Alert, Input, Button, Divider} from "antd";
import "./signIn.css";
import {Formik} from "formik";
import {useDispatch} from "react-redux";
import {Link, useNavigate} from "react-router-dom";
import {useLazyGetMeQuery, useLoginMutation} from "../../services/auth.api";
import {logout, setCredentials} from "../../redux/auth/auth.slice";
import {useEffect, useState, useRef} from "react";
import {setClassroom, setLoading} from "../../redux/app/app.slice";
import {loginSchema} from "./form.schema";
import remediaLogo from "../../assets/images/remedia-logo.png";
import {InfoCircleOutlined, GoogleOutlined} from "@ant-design/icons";
import { v4 as uuidv4 } from 'uuid';

// Get the API base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const SignIn = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [login, { isLoading }] = useLoginMutation();
    const [trigger] = useLazyGetMeQuery();
    const [errorMessage, setErrorMessage] = useState(null);
    const [googleAuthLoading, setGoogleAuthLoading] = useState(false);
    const googleWindow = useRef(null);

    useEffect(() => {
        dispatch(logout())
        dispatch(setClassroom(null));
    }, []);

    useEffect(() => {
        dispatch(setLoading(isLoading || googleAuthLoading));
    }, [isLoading, googleAuthLoading, dispatch]);

    // Listen for messages from the popup window
// Add this to your SignIn component

// Enhanced message listener with improved debugging
    useEffect(() => {
        const handleMessage = async (event) => {
            // Debug logging
            console.log('Message received:', event);
            console.log('Message origin:', event.origin);
            console.log('Current origin:', window.location.origin);
            console.log('Message data:', event.data);

            // Process authentication callback messages
            if (event.data && event.data.type === 'GOOGLE_AUTH_CALLBACK') {
                console.log('Processing Google auth callback');

                // Close the popup window reference if it exists
                if (googleWindow.current) {
                    googleWindow.current.close();
                }

                // Handle authentication errors
                if (event.data.error) {
                    console.error('Authentication error:', event.data.message);
                    setErrorMessage("Google authentication failed: " + (event.data.message || "Unknown error"));
                    setGoogleAuthLoading(false);
                    return;
                }

                // Process successful authentication
                if (event.data.token && event.data.refresh_token) {
                    console.log('Authentication successful, processing tokens');
                    try {
                        // Store tokens in Redux
                        dispatch(setCredentials({
                            token: event.data.token,
                            refresh_token: event.data.refresh_token
                        }));

                        console.log('Tokens stored in Redux, fetching user info');

                        // Get user info
                        const {data: user, error} = await trigger();

                        if (error) {
                            console.error('Error fetching user info:', error);
                            setErrorMessage("Could not retrieve user information");
                        } else if (user) {
                            console.log('User info received:', user);
                            dispatch(setCredentials({
                                token: event.data.token,
                                refresh_token: event.data.refresh_token,
                                user
                            }));

                            // Navigate to home page
                            console.log('Authentication complete, navigating to home');
                            navigate('/');
                        } else {
                            console.error('No user data received');
                            setErrorMessage("Could not retrieve user information");
                        }
                    } catch (error) {
                        console.error('Error processing authentication:', error);
                        setErrorMessage("Error processing authentication: " + (error.message || "Unknown error"));
                    } finally {
                        setGoogleAuthLoading(false);
                    }
                } else {
                    console.error('Invalid auth data received:', event.data);
                    setErrorMessage("Invalid authentication data received");
                    setGoogleAuthLoading(false);
                }
            }
        };

        // Add event listener for messages
        console.log('Adding message event listener');
        window.addEventListener('message', handleMessage);

        // Cleanup on unmount
        return () => {
            console.log('Removing message event listener');
            window.removeEventListener('message', handleMessage);
        };
    }, [dispatch, navigate, trigger, googleWindow]);
    const handlePostLogin = async ({ token, refresh_token }) => {
        const {data: user, error} = await trigger();
        if(error){
            setErrorMessage("An error occurred during login")
        }
        if(user){
            dispatch(setCredentials({ token, refresh_token, user }));
            navigate('/');
        }
    };

    const handleGoogleLogin = async () => {
        try {
            setGoogleAuthLoading(true);
            setErrorMessage(null);

            // Generate a state token to prevent CSRF attacks
            const stateToken = uuidv4();
            localStorage.setItem('googleAuthState', stateToken);

            // Use the API base URL for the request
            const response = await fetch(`${API_BASE_URL}/connect/google?state=${stateToken}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            // Log response details for debugging
            console.log('Google auth response status:', response.status);
            console.log('Content-Type:', response.headers.get('content-type'));

            if (!response.ok) {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
                } else {
                    const errorText = await response.text();
                    console.error('Error response text:', errorText);
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
            }

            const data = await response.json();
            console.log('Google auth data:', data);

            if (!data || !data.authorizationUrl) {
                throw new Error('Failed to get authorization URL');
            }

            // Open popup window for Google auth
            const width = 500;
            const height = 600;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2.5;

            googleWindow.current = window.open(
                data.authorizationUrl,
                'googleAuth',
                `width=${width},height=${height},left=${left},top=${top}`
            );

            if (googleWindow.current) {
                googleWindow.current.focus();

                // Check if popup was closed by user
                const checkClosed = setInterval(() => {
                    if (googleWindow.current && googleWindow.current.closed) {
                        clearInterval(checkClosed);
                        setGoogleAuthLoading(false);
                    }
                }, 1000);
            } else {
                throw new Error("Popup window was blocked. Please allow popups for this site.");
            }
        } catch (error) {
            setErrorMessage("Failed to initiate Google authentication: " + error.message);
            setGoogleAuthLoading(false);
        }
    };

    return (
        <main>
            <img src={remediaLogo} alt="remedia logo" className="__remediaLogo"/>
            <div className="container">
                <div className="login __box">
                    <h1 className="login__title __title"> Sign in to Remedia </h1>
                    <h2 className="login__subTitle __mixSubTitle"> We are delighted to see you again! </h2>

                    <Formik
                        initialValues={{ email: '', password: '' }}
                        validationSchema={loginSchema}
                        onSubmit={async (values, { setSubmitting }) => {
                            const { data, error } = await login(values);
                            if (error) {
                                setErrorMessage("An error occurred during login")
                                setSubmitting(false);
                            }
                            if (data) {
                                await handlePostLogin(data);
                            }
                        }}
                    >
                        {({
                              values,
                              errors,
                              touched,
                              handleChange,
                              handleBlur,
                              handleSubmit,
                              isSubmitting,
                          }) => (
                            <form onSubmit={handleSubmit} className="login__form">
                                {errorMessage && (
                                    <Alert
                                        message={errorMessage}
                                        type="error"
                                        icon={<InfoCircleOutlined />}
                                        showIcon
                                        className="__errorField"
                                    />
                                )}

                                <Input
                                    placeholder="Email"
                                    size="large"
                                    type="email"
                                    name="email"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.email}
                                    className="login__form-email"
                                />
                                {errors.email && touched.email && errors.email}

                                <Link to="/password-resets" className="login__form-forgot">Forgot password?</Link>

                                <Input.Password
                                    placeholder="Password"
                                    type="password"
                                    size="large"
                                    name="password"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.password}
                                    className="login__form-password"
                                />
                                {errors.password && touched.password && errors.password}

                                <button
                                    type="submit"
                                    disabled={isSubmitting || googleAuthLoading}
                                    className="primary login__form-btn __authBtn"
                                >
                                    Submit
                                </button>

                                <Divider>OR</Divider>

                                <Button
                                    icon={<GoogleOutlined />}
                                    onClick={handleGoogleLogin}
                                    disabled={isSubmitting || googleAuthLoading}
                                    className="google-login-btn"
                                    size="large"
                                    block
                                >
                                    Sign in with Google
                                </Button>
                            </form>
                        )}
                    </Formik>

                    <span className="login__otherOptions-register">
            Don't have an account? <Link to="/sign-up">Sign up</Link>
          </span>
                </div>
            </div>
        </main>
    );
};

export default SignIn;